import React, { useState, useEffect } from 'react';
import { getWorkflowTranscript } from 'services/api/WorkflowServiceAPI';
import { Modal } from 'common/components/Modal';
import { CallInfo } from 'types/agentCommandCenter.type';
import './TranscriptModal.scss';

interface TranscriptEntry {
    speaker: string;
    text: string;
    timestamp_iso?: string;
    order?: number;
}

interface TranscriptResponse {
    speeches: TranscriptEntry[];
}

interface TranscriptModalProps {
    isOpen: boolean;
    onClose: () => void;
    callId: string | null;
    callInfo?: CallInfo;
    isLoading?: boolean;
    callDetailsNavigation?: React.ReactNode;
}

/**
 * Reusable component for displaying call transcripts
 */
const TranscriptModal: React.FC<TranscriptModalProps> = ({
    isOpen,
    onClose,
    callId,
    callInfo = {},
    isLoading,
    callDetailsNavigation,
}) => {
    const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch transcript data when callId changes
    useEffect(() => {
        if (!isOpen || !callId) return;

        const fetchTranscript = async () => {
            try {
                setLoading(true);
                setError(null);

                const transcript = await getWorkflowTranscript(callId);

                // Process the transcript data
                if (transcript?.speeches && Array.isArray(transcript.speeches)) {
                    setTranscript(transcript.speeches);
                } else {
                    // If no data or invalid format, set empty array
                    setTranscript([]);
                }
            } catch (err) {
                console.error('Error fetching transcript:', err);
                // setError('Failed to load transcript. Please try again.');
                setTranscript([]);
            } finally {
                setLoading(false);
            }
        };

        fetchTranscript();
    }, [isOpen, callId]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={
                <div className='flex justify-between items-center'>
                    <h2>Call Transcript</h2>
                    {callDetailsNavigation}
                </div>
            }
            size='lg'
            showDefaultFooter={false}
            footer={
                <button onClick={onClose} className='btn'>
                    Close
                </button>
            }
            isLoadingOverlay={isLoading}
        >
            <div className='transcript-header'>
                <div className='transcript-title'>{callInfo?.name || 'Unknown Contact'}</div>
                <div className='transcript-meta'>
                    <span>Date: {callInfo?.callDate || 'Unknown'}</span>
                    <span>Time: {callInfo?.callTime || 'Unknown'}</span>
                </div>
            </div>

            {loading ? (
                <div className='transcript-empty'>Loading transcript...</div>
            ) : error || !transcript.length ? (
                <div className='transcript-empty notification'>Transcript not available yet - check back later</div>
            ) : transcript.length > 0 ? (
                <div className='transcript-container'>
                    {transcript.map((entry, index) => {
                        // Handle the API response format
                        const isAgent = entry.speaker === 'assistant';
                        const text = entry.text;

                        // Format the timestamp if available
                        let formattedTime = '';
                        if (entry.timestamp_iso) {
                            const date = new Date(entry.timestamp_iso);
                            formattedTime = date.toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                            });
                        }

                        return (
                            <div key={entry.order || index} className='transcript-message-wrapper'>
                                <div className={`transcript-message ${isAgent ? 'assistant' : 'customer'}`}>
                                    <div className='transcript-sender'>
                                        {isAgent ? 'SLAI Agent' : callInfo?.name || 'Customer'}
                                    </div>
                                    <div>{text}</div>
                                    {formattedTime && <div className='transcript-time'>{formattedTime}</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className='transcript-empty'>No transcript data available for this call.</div>
            )}
        </Modal>
    );
};

export default TranscriptModal;
